import IconCheck from "@components/ui/icons/IconCheck"
import { Link } from "@components/ui/link"
import { Text } from "@components/ui/typograhpy/Text"
import { cn } from "@lib/cn"
import { useDispatch, useToasts } from "@lib/store"
import { m, useMotionValue, useTransform } from "framer-motion"
import { FC, useEffect, useRef } from "react"
import { getTransformProperties } from "./helpers/get-transform-properties"

export type ToastActionVariant = "progress-modal" | "progress-modal-errors"

type ToastWithAction = {
  buttonAction?: ToastActionVariant
  buttonText?: string
  handleClickButton?: () => void
}

type ToastWithoutAction = {
  buttonAction?: never
  buttonText?: never
  handleClickButton?: never
}

type ToastVariant = "success" | "info" | "error"

type ToastType = ToastWithoutAction | ToastWithAction

export type ToastProps = ToastType & {
  variant: ToastVariant
  title: string
  handleRequestClose: () => void
  children?: never
  index: number
  isFirstVisible: boolean
  isWrapperExpanded: boolean
}

export const Toast: FC<ToastProps> = ({
  variant,
  title,
  handleRequestClose,
  handleClickButton,
  buttonAction,
  buttonText,
  isFirstVisible,
  isWrapperExpanded,
  index,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { maxHeight } = useToasts()
  const dispatch = useDispatch()
  const x = useMotionValue(0)
  const opacity = useTransform(x, [-100, -50, 50, 100], [1, 1, 1, 0])

  const transform = getTransformProperties(index, maxHeight)

  useEffect(() => {
    //
    // We need the height of the frontmost toast to calculate the transformations applied to other visible toasts
    //
    if (isFirstVisible && ref.current) {
      const { height } = ref.current.getBoundingClientRect()
      dispatch({ type: "set-max-height-toast", maxHeight: height })
    }
  }, [isFirstVisible, ref, dispatch])

  return (
    <m.div
      layout
      drag="x"
      dragConstraints={{ left: 0, right: 100 }}
      dragElastic={0.05}
      onDragEnd={(_, info) => handleDragEnd(info.velocity.x)}
      dragSnapToOrigin
      style={{
        position: "relative",
        zIndex: 100 - index,
        opacity,
        x,
      }}
    >
      <div
        ref={ref}
        className={cn(
          "absolute bottom-0 flex max-h-[initial] w-full items-center justify-between border border-border bg-sidebar p-4",
          "animate-toast-enter rounded-md shadow-small transition-transform duration-300 ease-in-out",
          isWrapperExpanded &&
            cn(
              index === 1 && "translate-3d-[0,calc(-100%-10px),-2px]!",
              index === 2 && "translate-3d-[0,calc((2*-100%)-20px),-2px]!",
            ),
        )}
        style={{
          transform,
          maxHeight: index > 0 ? maxHeight : undefined,
          visibility: index > 2 ? "hidden" : "visible",
        }}
        data-toast-element
        data-hide-children={index > 0 ? "" : undefined}
      >
        <span
          data-toast-countdown-animation
          className={cn(
            "absolute bottom-0 left-0 h-px animate-fill rounded-md",
            "group-hover/toasts:animation-play-state-paused",
            variant === "success" && "bg-success",
            variant === "info" && "bg-white",
            variant === "error" && "bg-danger",
            isWrapperExpanded && "animation-play-state-paused",
          )}
          data-variant={variant}
          onAnimationEnd={handleRequestClose}
        />
        <span className="absolute -bottom-4 left-0 h-4 w-full" />
        <Text variant="regular" className="line-clamp-2 font-semibold">
          {title}
        </Text>
        {buttonAction && buttonText && handleClickButton && (
          <Link
            size="small"
            onClick={handleClickButton}
            className="ml-6 shrink-0"
          >
            {buttonText}
          </Link>
        )}
        {variant === "success" && !buttonAction && (
          <span className="ml-6 block shrink-0 text-success">
            <IconCheck size={20} />
          </span>
        )}
      </div>
    </m.div>
  )

  function handleDragEnd(velocityX: number) {
    if (velocityX > 150) {
      handleRequestClose()
    }
  }
}
